import React, { FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Card, Col } from 'antd';
import dayjs from 'dayjs';
import { getDayjsLocale } from 'i18n/dateLocale';
import useGetEnrichedPurchaseQuery from 'apollo/resolvers/resultPurchase/get';
import Button from 'components/generalUI/Button';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import placeholderImage from 'assets/images/placeholder-image.png';
import styles from './SuccessPurchaseCard.module.scss';
import ViewTicketsButton from './ViewTicketsButton';

dayjs.extend(customParseFormat);

interface SuccessPurchaseCardProps {
  purchaseId: string;
  email: string;
  uuid: string;
}

const boldComponents = { b: <b /> };

const SuccessPurchaseCard: FC<SuccessPurchaseCardProps> = (
  props: SuccessPurchaseCardProps
) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();

  const { purchaseId, email, uuid } = props;
  const { data } = useGetEnrichedPurchaseQuery(uuid);
  const [displayDate, setDisplayDate] = useState<string>('');

  const purchase = data?.getEnrichedPurchase.purchase;
  const isGiftCardPurchase = !!purchase?.giftCard;
  const clientEmail = data?.getEnrichedPurchase.client?.email || email;

  const ticketImage =
    purchase?.packPurchases?.[0]?.image ||
    purchase?.entranceTickets?.[0]?.image ||
    placeholderImage;

  useEffect(() => {
    const visitDate = purchase?.visitDate;
    if (visitDate) {
      const date = dayjs(visitDate, 'DD/MM/YYYY')
        .locale(getDayjsLocale(i18n.language))
        .format('D MMMM YYYY');
      setDisplayDate(date);
    }
    // i18n.language so the date re-formats when the language changes
  }, [data, i18n.language]);

  const waitingForYouText = () => {
    if (isGiftCardPurchase) {
      return <>{t('checkout.success.waitingForYouGiftCard')}</>;
    }
    return (
      <>
        {t('checkout.success.waitingForYou')} {displayDate}
      </>
    );
  };

  return (
    <Col>
      <Card className={styles.Card}>
        <h3 className={styles.CardTitle}>{waitingForYouText()}</h3>

        <p className={styles.OrderNumber}>
          {t('checkout.success.orderNumber')} <b>#{purchaseId}</b>
        </p>

        {isGiftCardPurchase ? (
          <p className={styles.Description}>
            {t('checkout.success.purchaseMainDescriptionGiftCard')} <b>{clientEmail}</b>
          </p>
        ) : (
          <>
            <img className={styles.TicketImage} src={ticketImage} alt="" />

            <div className={styles.Description}>
              <p className={styles.Paragraph}>
                <Trans
                  i18nKey="checkout.success.purchaseMainDescription"
                  components={boldComponents}
                />
              </p>
              <p className={styles.Paragraph}>
                <Trans
                  i18nKey="checkout.success.downloadTicketsDescription"
                  components={boldComponents}
                />{' '}
                <b>{clientEmail}</b>
              </p>
              <p className={styles.Paragraph}>
                <Trans
                  i18nKey="checkout.success.purchaseSecondaryDescription"
                  components={boldComponents}
                />
              </p>
            </div>

            <div className={styles.Actions}>
              <Button kind="secondary" size="large" onClick={() => history.push('/')}>
                {t('checkout.success.backToHome')}
              </Button>
              <ViewTicketsButton uuid={uuid} />
            </div>

            <p className={styles.Reminder}>{t('checkout.success.reminder')}</p>
          </>
        )}
      </Card>
    </Col>
  );
};

export default SuccessPurchaseCard;
